import { useState, useCallback, useMemo } from 'react';
import { ReferralData } from '../types';
import { tryLeadLock, removeLeadLock } from 'src/services/leadManagementApi';
import { logger } from '../../../logger';

interface UseReferralDataProps {
  currentUser: string;
  maxLockedRecords?: number;
  /** Called after user successfully locks or unlocks. On lock: (leadId). On unlock: no args. */
  onLockOrUnlock?: (lockedLeadId?: string) => void;
  /** Called after a successful unlock to clear all filters so the user sees all records again. */
  onAutoClearFiltersOnUnlock?: () => void;
}

const formatLockedAt = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(
    2,
    '0'
  )}/${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(
    now.getMinutes()
  ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
};

export type LockAlert = { severity: 'success' | 'error'; message: string } | null;

export const useReferralData = ({
  currentUser,
  maxLockedRecords = 1,
  onLockOrUnlock,
  onAutoClearFiltersOnUnlock,
}: UseReferralDataProps) => {
  const [data, setData] = useState<ReferralData[]>([]);
  const [lockLoadingLeadId, setLockLoadingLeadId] = useState<string | null>(null);
  const [lockAlert, setLockAlert] = useState<LockAlert>(null);

  const lockedRecordsCount = useMemo(() => {
    return data.filter((item) => item.lockedBy === currentUser).length;
  }, [data, currentUser]);

  const handleSelectRow = useCallback(
    (id: string) => {
      setData((prevData) =>
        prevData.map((item) => {
          if (item.id === id && item.lockedBy && item.lockedBy !== currentUser) {
            return item; // Don't allow selecting/deselecting records locked by others
          }
          return item.id === id ? { ...item, selected: !item.selected } : item;
        })
      );
    },
    [currentUser]
  );

  const handleSelectAll = useCallback(
    (checked: boolean, visibleIds: string[]) => {
      setData((prevData) =>
        prevData.map((item) => {
          if (item.lockedBy && item.lockedBy !== currentUser) {
            return item; // Don't allow selecting/deselecting records locked by others
          }
          return visibleIds.includes(item.id) ? { ...item, selected: checked } : item;
        })
      );
    },
    [currentUser]
  );

  const handlePatientCCIdChange = useCallback((id: string, newValue: string) => {
    setData((prevData) =>
      prevData.map((item) => (item.id === id ? { ...item, patientCCId: newValue } : item))
    );
  }, []);

  const handleStatusChange = useCallback((id: string, newValue: string) => {
    setData((prevData) =>
      prevData.map((item) => (item.id === id ? { ...item, status: newValue } : item))
    );
  }, []);

  const handleLockToggle = useCallback(
    async (leadId: string) => {
      const record = data.find((item) => String(item.leadId ?? item.id) === String(leadId));
      if (!record) return;

      setLockLoadingLeadId(leadId);
      setLockAlert(null);

      const isUnlockingOwnLock = record.lockedBy === currentUser;

      const clearLoading = () => setLockLoadingLeadId(null);

      // Prevent all lock/unlock actions on flagged records (auto-unlock happens on flag)
      if (record.isFlaggedRecord) {
        setLockAlert({
          severity: 'error',
          message: 'Locking is not allowed for flagged records.',
        });
        clearLoading();
        return;
      }

      if (isUnlockingOwnLock) {
        try {
          await removeLeadLock(leadId, currentUser);
          logger.info('LMA: unlock success', {
            action: 'unlock',
            leadId,
            userName: currentUser,
            payload: {},
          });
          setData((prevData) =>
            prevData.map((item) =>
              String(item.leadId ?? item.id) === String(leadId)
                ? { ...item, lockedBy: null, lockedAt: null }
                : item
            )
          );
          setLockAlert({
            severity: 'success',
            message: `Unlocked successfully. Lead ID: ${leadId}`,
          });
          onLockOrUnlock?.();
          onAutoClearFiltersOnUnlock?.();
        } catch (err: any) {
          logger.error('LMA: unlock failed', {
            action: 'unlock',
            leadId,
            userName: currentUser,
            error: err?.response?.data?.error || err?.message,
            payload: {},
          });
          const msg =
            err?.response?.data?.error || err?.message || 'Failed to unlock. Please try again.';
          setLockAlert({ severity: 'error', message: msg });
        } finally {
          clearLoading();
        }
        return;
      }

      try {
        const result = await tryLeadLock(leadId, currentUser, maxLockedRecords);
        if (!result.success && result.status === 'MAX_REACHED') {
          setLockAlert({
            severity: 'error',
            message:
              'You have reached the max number of locks available. To know the items locked by you please go to the filters section and click Locked by me.',
          });
          clearLoading();
          return;
        }
        if (!result.success && result.status === 'LOCKED_BY_OTHER') {
          setLockAlert({
            severity: 'error',
            message: `This row is locked by ${result.lockedBy}.`,
          });
          setData((prevData) =>
            prevData.map((item) =>
              String(item.leadId ?? item.id) === String(leadId)
                ? { ...item, lockedBy: result.lockedBy, lockedAt: null }
                : item
            )
          );
          clearLoading();
          return;
        }
        if (!result.success) {
          setLockAlert({
            severity: 'error',
            message: 'Failed to update lock. Please try again.',
          });
          clearLoading();
          return;
        }
        logger.info('LMA: lock success', {
          action: 'lock',
          leadId,
          userName: currentUser,
          payload: {},
        });
        setData((prevData) =>
          prevData.map((item) =>
            String(item.leadId ?? item.id) === String(leadId)
              ? { ...item, lockedBy: currentUser, lockedAt: formatLockedAt() }
              : item
          )
        );
        setLockAlert({
          severity: 'success',
          message:
            result.status === 'ALREADY_OWNED'
              ? `This row is already locked by you. Lead ID: ${leadId}`
              : `Locked successfully. Lead ID: ${leadId}`,
        });
        onLockOrUnlock?.(leadId);
      } catch (err: any) {
        logger.error('LMA: lock failed', {
          action: 'lock',
          leadId,
          userName: currentUser,
          error: err?.response?.data?.error || err?.message,
          payload: {},
        });
        setLockAlert({
          severity: 'error',
          message:
            err?.response?.data?.error ||
            err?.message ||
            'Failed to update lock. Please try again.',
        });
      } finally {
        clearLoading();
      }
    },
    [currentUser, maxLockedRecords, data, onLockOrUnlock, onAutoClearFiltersOnUnlock]
  );

  return {
    data,
    setData,
    lockedRecordsCount,
    handleSelectRow,
    handleSelectAll,
    handlePatientCCIdChange,
    handleStatusChange,
    handleLockToggle,
    lockLoadingLeadId,
    lockAlert,
    setLockAlert,
  };
};
